import { useTranslation } from 'react-i18next';
import { Card, Divider, Flex, Grid, Loader, Text, Title } from '@mantine/core';
import moment from 'moment';
import { BarChart } from '@mantine/charts';
import { useMemo } from 'react';
import { useGetPopularDaysStatisticsReport } from '../../../hooks/reports/queries';
import momentTz from 'moment-timezone';
import { CommonReportChartProps, graphColors } from '.';

type DayKey = 'Friday' | 'Monday' | 'Saturday' | 'Sunday' | 'Thursday' | 'Tuesday' | 'Wednesday';

const PopularDaysChart = ({ dateRange, chartHeight }: CommonReportChartProps) => {
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const { data, isLoading } = useGetPopularDaysStatisticsReport(searchParamStoreId, {
    fromDate: dateRange[0]?.toISOString(),
    toDate: dateRange[1]?.toISOString(),
    timezone: momentTz.tz.guess(true)
  });
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons', 'Notifications']);

  const chartData = useMemo(() => {
    return Object.keys(data?.popularDaysReport || {}).map((dayKey) => {
      const randomColor = graphColors[Math.floor(Math.random() * graphColors.length)].hex;
      return {
        name: t(`FieldName:dayNames.${dayKey.toLowerCase()}`),
        value: data?.popularDaysReport[dayKey as DayKey] || 0,
        color: randomColor
      };
    });
  }, [data]);

  return (
    <Card h="100%">
      <Card.Section inheritPadding withBorder py="md">
        <Grid>
          <Grid.Col span={12}>
            <Title>{t('ReportsPage:popularDaysReport.title')}</Title>
            <Text c={'dimmed'}>
              {moment(dateRange[0]).format('DD MMMM YYYY')} -{' '}
              {moment(dateRange[1]).format('DD MMMM YYYY')}
            </Text>
          </Grid.Col>
          {isLoading && <Loader m={'auto'} />}
          {!isLoading && (
            <>
              <Grid.Col>
                <BarChart
                  h={chartHeight}
                  data={chartData}
                  dataKey="name"
                  series={[{ name: 'value', label: t('FieldName:orders') }]}
                  tickLine="y"
                  orientation="vertical"
                  yAxisProps={{ width: 80 }}
                  barProps={{ radius: 10 }}
                />
              </Grid.Col>
              <Grid.Col span={12}>
                {Object.keys(data?.popularDaysReport || {})?.map((dayKey) => {
                  return (
                    <>
                      <Divider size="sm" my="xs" />
                      <Flex justify="space-between" align="center">
                        <Text>{t(`FieldName:dayNames.${dayKey.toLocaleLowerCase()}`)}</Text>
                        <Text>{data?.popularDaysReport[dayKey as DayKey]}</Text>
                      </Flex>
                    </>
                  );
                })}
              </Grid.Col>
            </>
          )}
        </Grid>
      </Card.Section>
    </Card>
  );
};

export default PopularDaysChart;
